(function () {
  class DestinationsSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.dsearch__input');
      this.dropdown = this.querySelector('.dsearch__dropdown');
      this.countries = [];
      this.regions = [];
      this.isLoading = false;
      this.isDataLoaded = false;
      this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
      this.lastFetch = 0;
      this.debounceTimer = null;

      this.input?.addEventListener('input', this.onInput.bind(this));
      this.input?.addEventListener('focus', this.onFocus.bind(this));
      this.input?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hide();
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.hide();
          return;
        }
        if (e.target.tagName === 'A') {
          e.preventDefault();
          this.sendSearchEventLog(e.target.innerText, true);
          window.location.href = e.target.href;
        }
      });

      this.storefrontToken =
        this.getAttribute('data-token') || window?.themeSettings?.storefrontToken;

      if (!this.storefrontToken) {
      }

      // Preload data silently in the background for better performance
      this.preloadDataSilently();
    }

    debounce(fn, delay) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    onFocus() {
      // Show popular destinations when input is focused (if no search query)
      // Only show popular destinations on homepage search
      const q = this.input.value.trim();
      const isHomepageSearch = !!this.closest('.homepage-search-container');
      if (!q && isHomepageSearch) {
        this.showPopularDestinations();
      }
    }

    onInput() {
      const q = this.input.value.trim().toLowerCase();
      if (!q) return this.hide();

      if (!this.storefrontToken) {
        return;
      }

      // If data is already loaded, search immediately
      if (this.isDataLoaded) {
        this.searchLocal(q);
        return;
      }

      // If data is loading, show loading state
      if (this.isLoading) {
        this.showLoading();
        return;
      }

      // If data needs refresh, reload it
      if (this.needsRefresh()) {
        this.preloadData().then(() => this.searchLocal(q));
        return;
      }

      // Debounce the search to avoid too many API calls
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.searchLive(q), 300);
    }

    async searchLive(q) {
      // This method is now only used as a fallback
      // The main search logic is in searchLocal()
      await this.preloadData();
      this.searchLocal(q);
    }

    render(countries, regions = []) {
      if (!countries.length && !regions.length) {
        this.dropdown.innerHTML = '<div class="dsearch__group-title">No matches</div>';
        this.dropdown.hidden = false;
        return;
      }

      let html = '';

      // Render countries
      if (countries.length > 0) {
        const countryListHtml = countries
          .map((country) => {
            const icon = country.icon_url
              ? `<img src="${country.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;">`
              : '';
            return `<li class="dsearch__item"><a class="dsearch__link" href="${country.url}">${icon}${country.title}</a></li>`;
          })
          .join('');
        html += `<div class="dsearch__group-title">Country</div><ul class="dsearch__list">${countryListHtml}</ul>`;
      }

      // Render regions
      if (regions.length > 0) {
        const regionListHtml = regions
          .map((region) => {
            const icon = region.icon_url
              ? `<img src="${region.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;">`
              : '';
            return `<li class="dsearch__item"><a class="dsearch__link ${region.title === 'Global' && `new-search__item`}" href="${region.url}">${icon}${region.title}</a></li>`;
          })
          .join('');
        html += `<div class="dsearch__group-title">Region</div><ul class="dsearch__list">${regionListHtml}</ul>`;
      }

      this.dropdown.innerHTML = html;
      this.dropdown.hidden = false;
    }

    showLoading() {
      this.dropdown.innerHTML = '<div class="dsearch__group-title">Searching...</div>';
      this.dropdown.hidden = false;
    }

    showNoResults() {
      this.dropdown.innerHTML =
        '<div class="dsearch__group-title no-results">No results. Try searching another country or region.</div>';
      this.dropdown.hidden = false;
    }

    showError(message) {
      this.dropdown.innerHTML = `<div class="dsearch__group-title" style="color: #e74c3c;">${message}</div>`;
      this.dropdown.hidden = false;
    }

    async fetchAllCountries() {
      const allCountries = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const query = `query FetchCountries($first: Int!, $after: String) {
          metaobjects(type: "destination_country", first: $first, after: $after) {
            edges {
              node {
                handle
                fields { key value }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }`;

        const variables = { first: 50, after: cursor };

        const res = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const json = await res.json();

        if (json.errors) {
          throw new Error('Failed to fetch countries');
        }

        const countries = json.data?.metaobjects?.edges || [];
        allCountries.push(...countries);

        hasNextPage = json.data?.metaobjects?.pageInfo?.hasNextPage || false;
        cursor = json.data?.metaobjects?.pageInfo?.endCursor;
      }

      return allCountries;
    }

    async fetchAllRegions() {
      const allRegions = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const query = `query FetchRegions($first: Int!, $after: String) {
          metaobjects(type: "destination_region", first: $first, after: $after) {
            edges {
              node {
                handle
                fields { 
                  key 
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url(transform: {maxWidth: 32, maxHeight: 32, crop: CENTER})
                        altText
                        originalSrc
                      }
                    }
                  }
                }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }`;

        const variables = { first: 50, after: cursor };

        const res = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const json = await res.json();

        if (json.errors) {
          throw new Error('Failed to fetch regions');
        }

        const regions = json.data?.metaobjects?.edges || [];
        allRegions.push(...regions);

        hasNextPage = json.data?.metaobjects?.pageInfo?.hasNextPage || false;
        cursor = json.data?.metaobjects?.pageInfo?.endCursor;
      }

      return allRegions;
    }

    async preloadData() {
      if (this.isDataLoaded && !this.needsRefresh()) {
        return Promise.resolve();
      }

      this.isLoading = true;
      this.showLoading();

      try {
        const [allCountries, allRegions] = await Promise.all([
          this.fetchAllCountries(),
          this.fetchAllRegions(),
        ]);

        this.countries = this.processCountries(allCountries);
        this.regions = this.processRegions(allRegions);
        this.isDataLoaded = true;
        this.lastFetch = Date.now();
      } catch (e) {
        this.showError('Failed to load search data');
      } finally {
        this.isLoading = false;
      }
    }

    async preloadDataSilently() {
      if (this.isDataLoaded && !this.needsRefresh()) {
        return Promise.resolve();
      }

      this.isLoading = true;
      // Don't show loading state for silent preloading

      try {
        const [allCountries, allRegions] = await Promise.all([
          this.fetchAllCountries(),
          this.fetchAllRegions(),
        ]);

        this.countries = this.processCountries(allCountries);
        this.regions = this.processRegions(allRegions);
        this.isDataLoaded = true;
        this.lastFetch = Date.now();
      } catch (e) {
      } finally {
        this.isLoading = false;
      }
    }

    needsRefresh() {
      return !this.isDataLoaded || Date.now() - this.lastFetch > this.cacheExpiry;
    }

    searchLocal(q) {
      const searchTerm = q.toLowerCase();
      this.sendSearchEventLog(q);

      const countries = this.countries.filter((item) => {
        const matchesSearch = item.search.includes(searchTerm);
        const matchesTitle = item.title.toLowerCase().includes(searchTerm);
        return matchesSearch || matchesTitle;
      });

      const regions = this.regions.filter((item) => {
        const matchesSearch = item.search.includes(searchTerm);
        const matchesTitle = item.title.toLowerCase().includes(searchTerm);
        return matchesSearch || matchesTitle;
      });

      // Find regions linked to matching countries
      // Initialize seenRegionTitles with already filtered regions to prevent duplicates
      const matchingRegions = [];
      const seenRegionTitles = new Set(regions.map((region) => region.title));

      countries.forEach((country) => {
        // Try to find region by title first
        if (country.region_title && !seenRegionTitles.has(country.region_title)) {
          seenRegionTitles.add(country.region_title);
          matchingRegions.push({
            title: country.region_title,
            search: country.region_title.toLowerCase(),
            url: country.region_url || '',
            icon_url: country.region_icon_url || '',
          });
        } else if (country.region_reference) {
          // If we have a region reference but no title, try to find the region by reference

          // Extract the region ID from the GID
          const regionId = country.region_reference.split('/').pop();

          // Try to find the region in our regions data by ID
          let foundRegion = this.regions.find((region) => region.id === regionId);

          // If not found by ID, try to match by common patterns
          if (!foundRegion) {
            // Common region mappings for countries
            const regionMappings = {
              australia: 'oceania',
              'new zealand': 'oceania',
              philippines: 'asia',
              japan: 'asia',
              china: 'asia',
              thailand: 'asia',
              singapore: 'asia',
              malaysia: 'asia',
              indonesia: 'asia',
              'south korea': 'asia',
              taiwan: 'asia',
              vietnam: 'asia',
              india: 'asia',
              france: 'europe',
              germany: 'europe',
              italy: 'europe',
              spain: 'europe',
              'united kingdom': 'europe',
              england: 'europe',
              netherlands: 'europe',
              switzerland: 'europe',
              austria: 'europe',
              belgium: 'europe',
              portugal: 'europe',
              greece: 'europe',
              norway: 'europe',
              sweden: 'europe',
              denmark: 'europe',
              finland: 'europe',
              poland: 'europe',
              'czech republic': 'europe',
              hungary: 'europe',
              croatia: 'europe',
              'united states': 'north america',
              canada: 'north america',
              mexico: 'north america',
              brazil: 'south america',
              argentina: 'south america',
              chile: 'south america',
              'south africa': 'africa',
              egypt: 'africa',
              morocco: 'africa',
              kenya: 'africa',
              tanzania: 'africa',
            };

            const countryName = country.title.toLowerCase();
            const expectedRegion = regionMappings[countryName];

            if (expectedRegion) {
              foundRegion = this.regions.find((region) =>
                region.title.toLowerCase().includes(expectedRegion),
              );
            }
          }

          if (foundRegion && !seenRegionTitles.has(foundRegion.title)) {
            seenRegionTitles.add(foundRegion.title);
            matchingRegions.push(foundRegion);
          }
        }
      });

      const countryResults = countries;
      const regionResults = [...regions, ...matchingRegions];

      if (countryResults.length > 0 || regionResults.length > 0) {
        this.render(countryResults, regionResults);
      } else {
        this.showNoResults();
      }
    }

    sendSearchEventLog(searchTerm, clicked = false) {
      // Clear previous timer to debounce
      clearTimeout(this.debounceTimer);

      const time = clicked ? 0 : 1000; // 1-second debounce

      this.debounceTimer = setTimeout(() => {
        const endpoint = 'https://portal.simify.com/api/v2/webhooks/customEvent';

        const country = Shopify.country;
        const payload = {
          eventSource: 'search_input',
          eventType: 'search_term',
          additionalInfo: (clicked ? `${searchTerm};clicked` : searchTerm) + `;${country}`,
        };

        const data = JSON.stringify(payload);

        try {
          if (navigator.sendBeacon) {
            // Fire-and-forget, non-blocking
            navigator.sendBeacon(endpoint, data);
          } else {
            // Fallback for older browsers
            fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: data,
              keepalive: true, // ensures it can complete during navigation
            });
          }
        } catch (err) {
          console.error('Error sending beacon:', err);
        }
      }, time);
    }

    processCountries(countries) {
      const toMap = (fields) => {
        const obj = {};
        (fields || []).forEach((f) => (obj[f.key] = f.value));
        return obj;
      };

      return countries.map((e) => {
        const m = toMap(e.node.fields);
        return {
          id: e.node.handle, // Store the handle for reference
          title: m.title || '',
          search: (m.search_title || m.title || '').toLowerCase(),
          url: m.url || '',
          icon_url: m.icon_url || '',
          region_title: m.region_title || '',
          region_url: m.region_url || '',
          region_icon_url: m.region_icon_url || '',
          region_reference: m.region || '',
        };
      });
    }

    processRegions(regions) {
      const toMap = (fields) => {
        const obj = {};
        (fields || []).forEach((f) => {
          obj[f.key] = f.value;

          // Special handling for icon field - check if it's a GID in the value
          if (
            f.key === 'icon' &&
            f.value &&
            typeof f.value === 'string' &&
            f.value.startsWith('gid://shopify/MediaImage/')
          ) {
            // We'll use the icon_url field instead, so just store the GID for reference
            obj[`${f.key}_gid`] = f.value;
          }
          // If this field has a reference to a MediaImage, extract the image URL
          if (f.reference) {
            if (f.reference.__typename === 'MediaImage' && f.reference.image) {
              if (f.reference.image.url) {
                obj[`${f.key}_url`] = f.reference.image.url;
              } else if (f.reference.image.originalSrc) {
                // Use the originalSrc and transform it
                const storeDomain = window.location.hostname;
                const imageUrl = `${f.reference.image.originalSrc}?v=1234567890&width=32&height=32&crop=center`;
                obj[`${f.key}_url`] = imageUrl;
              }
            } else if (f.reference.url) {
              obj[`${f.key}_url`] = f.reference.url;
            } else {
              // If we have a reference but no URL, try to get the GID and convert it
              const gid = f.reference.id || f.reference;
              if (gid && typeof gid === 'string' && gid.startsWith('gid://shopify/MediaImage/')) {
                // Extract the media ID from the GID
                const mediaId = gid.split('/').pop();
                // Try using the same pattern as the working destination grid
                const imageUrl = `//simify.com/cdn/shop/files/${mediaId}.png?v=1234567890&width=32&height=32&crop=center`;
                obj[`${f.key}_url`] = imageUrl;
              }
            }
          }
        });
        return obj;
      };

      return regions.map((e) => {
        const m = toMap(e.node.fields);
        const result = {
          id: e.node.handle, // Store the handle for reference
          title: m.link_title || m.title || '', // Use link_title for display (capitalized)
          search: (m.link_title || m.title || '').toLowerCase(),
          url: m.link_url || m.url || '', // Use link_url for the region URL
          icon_url: m.icon_url || '', // Use the manually added icon_url field
        };
        return result;
      });
    }

    hide() {
      this.dropdown.hidden = true;
      this.dropdown.innerHTML = '';
    }

    async showPopularDestinations() {
      if (!this.storefrontToken) {
        return;
      }

      try {
        const popularDestinations = await this.fetchPopularDestinations();
        this.renderPopularDestinations(popularDestinations);
      } catch (error) {}
    }

    async fetchPopularDestinations() {
      const query = `query FetchPopularDestinations {
        metaobjects(type: "popular_destination_tile", first: 20) {
          edges {
            node {
              handle
              fields {
                key
                value
                reference {
                  ... on MediaImage {
                    image {
                      url(transform: {maxWidth: 32, maxHeight: 32, crop: CENTER})
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }`;

      const response = await fetch('/api/2024-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      const filteredData = data.data.metaobjects.edges
        .filter((e) => e.node.fields.some((f) => f.key === 'position'))
        .sort((a, b) => {
          const getPosition = (node) => {
            const positionField = node.fields.find((f) => f.key === 'position');
            return positionField ? Number(positionField.value) : 0;
          };

          return getPosition(a.node) - getPosition(b.node);
        });

      return filteredData;
    }

    renderPopularDestinations(popularDestinations) {
      if (!popularDestinations.length) {
        this.dropdown.innerHTML =
          '<div class="dsearch__group-title">No popular destinations available</div>';
        this.dropdown.hidden = false;
        return;
      }

      let html =
        '<div class="dsearch__group-title">Popular Destinations</div><ul class="dsearch__list">';

      popularDestinations.forEach((destination) => {
        const fields = {};
        destination.node.fields.forEach((field) => {
          fields[field.key] = field.value;
          // Handle image references to get URLs
          if (
            field.reference &&
            field.reference.__typename === 'MediaImage' &&
            field.reference.image
          ) {
            fields[`${field.key}_url`] = field.reference.image.url;
          }
        });

        const title = fields.title || '';
        const imageUrl = fields.image_url || '';
        const iconUrl = fields.icon_url || '';
        const url = fields.link || '#';
        const newProduct = fields.new || false;

        // Prioritize icon_url field, fallback to image field
        const finalIconUrl = iconUrl || imageUrl;
        const icon = finalIconUrl
          ? `<img src="${finalIconUrl}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;">`
          : '';

        html += `<li class="dsearch__item"><a class="dsearch__link ${newProduct && `new-search__item`}"  href="${url}">${icon}${title}</a></li>`;
      });

      html += '</ul>';
      this.dropdown.innerHTML = html;
      this.dropdown.hidden = false;
    }
  }

  customElements.define('destinations-search', DestinationsSearch);
})();
